/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/FileAnalysis.tsx
 * DESCRIPTION: File upload and analysis interface for media verification.
 *              Provides drag-and-drop upload, watermarking options, and
 *              AI assessment configuration with real-time queue management.
 *              Live backend API integration.
 *              Supports single file upload, bulk upload (max 100 files),
 *              AI assessment options, watermarking, and real-time progress.
 *
 * FEATURES:
 * - Drag-and-drop file upload with progress tracking
 * - Visible and invisible watermarking options
 * - AI assessment provider selection (Mistral, Qwen, OpenAI)
 * - Custom assessment prompts
 * - File queue with status tracking
 * - Multi-file upload support
 * - Cancel/pause support for uploads
 * - Auto-redirect to identity page after single file upload
 * - Bulk upload: Progress bar stays visible, View Results per file
 * - URL submission for remote media analysis
 *
 * BACKEND INTEGRATION (Swagger v2.0.0):
 * - POST /v1/upload - Single file upload with assessment
 * - POST /v1/upload/bulk - Bulk file upload (max 100 files)
 * - POST /v1/upload/url - URL-based media analysis
 * - GET /v1/results - Poll for analysis results by job_id
 * - GET /v1/upload/bulk/{id} - Track bulk processing status
 * - DELETE /v1/upload/bulk/{id} - Cancel bulk upload
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 3.2.0
 * @created 2025-01-15
 * @updated 2026-03-26 - Redesigned with watermarking options, AI assessment, and file queue
 * @updated 2026-04-03 - Updated to match Swagger v2.0.0 backend API changes
 * @updated 2026-04-23 - Beta 1.1: Offline mode handling, auto-redirect,
 *                       cancel support, persistentId navigation, progress bar fixes
 * @updated 2026-06-11 - Mobile responsiveness: Fixed header overlap, responsive grids,
 *                       balanced spacing, disabled AI provider selection, mobile layout fixes.
 * @updated 2026-06-11 - File upload and analysis interface.
 *                       Features immediate processing on drop, dynamic file size
 *                       limits via env vars, and smart auto-redirect.
 *                       Includes robust queue visibility and analysis options.
 * @updated 2026-09-05 - Phase 2.3: Added org_id tagging to uploads for
 *                       workspace-scoped data segregation. Uses useOrgFilter
 *                       hook to automatically tag uploads with active org.
 * ─────────────────────────────────────────────────────────────────────
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast'; // Global toast survives navigation!
import {
  Upload, FileVideo, FileImage, FileAudio, FileText, X, Check, 
  AlertCircle, Cpu, Shield, Eye, EyeOff, Plus
} from 'lucide-react';
import { getApiClient } from '../lib/api/apiClient';
import { ENDPOINTS } from '../lib/api/endpoints';
import type { AnalysisResponse } from '../lib/api/types';
import { useOrgFilter } from '../hooks/useOrgFilter';

// ─────────────────────────────────────────────────────────────────────
// TYPES & CONFIG
// ─────────────────────────────────────────────────────────────────────
interface QueuedFile {
  id: string;
  file: File;
  preview?: string;
  status: 'queued' | 'uploading' | 'processing' | 'completed' | 'failed';
  progress: number;
  jobId?: string;
  persistentId?: string;
  error?: string;
}

interface UploadOptions {
  watermark: 'visible' | 'invisible' | 'both' | 'none';
  customPrompt: string;
}

// Read from .env (VITE_MAX_FILE_SIZE) with 10MB fallback
const MAX_FILE_SIZE = Number(import.meta.env.VITE_MAX_FILE_SIZE) || 10 * 1024 * 1024;
const MAX_FILE_SIZE_MB = Math.round(MAX_FILE_SIZE / (1024 * 1024));

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const FileAnalysis: React.FC = () => {
  const navigate = useNavigate();
  // Phase 2.3: Get active org_id for workspace-scoped uploads
  const orgFilter = useOrgFilter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isDragging, setIsDragging] = useState(false);
  const [queuedFiles, setQueuedFiles] = useState<QueuedFile[]>([]);
  const [uploadOptions, setUploadOptions] = useState<UploadOptions>({
    watermark: 'none',
    customPrompt: '',
  });
  const [activeTab, setActiveTab] = useState<'files' | 'options'>('files');
  
  // Ref to ensure we only auto-redirect ONCE per batch
  const hasRedirectedRef = useRef(false);

  // ───────────────────────────────────────────────────────────────────
  // URL INPUT STATE
  // ───────────────────────────────────────────────────────────────────
  const [urlInput, setUrlInput] = useState('');
  const [isUrlSubmitting, setIsUrlSubmitting] = useState(false);

  // ───────────────────────────────────────────────────────────────────
  // IMMEDIATE PROCESSING TRIGGER
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Find any files that are waiting in the queue and start uploading them immediately
    const pendingFiles = queuedFiles.filter(f => f.status === 'queued');
    if (pendingFiles.length > 0) {
      pendingFiles.forEach(file => {
        // Mark as uploading to prevent duplicate triggers
        setQueuedFiles(prev => prev.map(f => f.id === file.id ? { ...f, status: 'uploading' } : f));
        uploadSingleFile(file);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queuedFiles]);

  // ───────────────────────────────────────────────────────────────────
  // FILE HANDLING & VALIDATION
  // ───────────────────────────────────────────────────────────────────
  const handleFiles = useCallback((files: FileList | File[]) => {
    const newFiles: QueuedFile[] = [];
    
    for (const file of Array.from(files)) {
      // Validate file size against dynamic env limit
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit and was skipped.`);
        continue; 
      }
      
      newFiles.push({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        status: 'queued', // Will be picked up by useEffect and changed to 'uploading'
        progress: 0,
      });
    }

    if (newFiles.length > 0) {
      hasRedirectedRef.current = false; // Reset redirect tracker for this new batch
      setQueuedFiles(prev => [...prev, ...newFiles]);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  // ───────────────────────────────────────────────────────────────────
  // UPLOAD & POLLING LOGIC
  // ───────────────────────────────────────────────────────────────────
  const pollForResults = async (fileId: string, jobId: string, persistentId?: string) => {
    const apiClient = getApiClient();
    const maxAttempts = 60;
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        setQueuedFiles(prev => prev.map(f => 
          f.id === fileId ? { ...f, status: 'processing', progress: Math.min(60 + attempts * 0.5, 95) } : f
        ));

                const response = await apiClient.get<AnalysisResponse>(ENDPOINTS.RESULTS, { job_id: jobId });

        if (response.status === 'completed') {
          // Extract persistent_id from polling response (URL uploads don't have it at submit time)
          const resolvedPersistentId = persistentId || (response as any).persistent_id;

          setQueuedFiles(prev => prev.map(f => 
            f.id === fileId ? { ...f, status: 'completed', progress: 100, persistentId: resolvedPersistentId } : f
          ));

          // ───────────────────────────────────────────────────────────
          // SMART AUTO-REDIRECT & GLOBAL NOTIFICATION
          // ───────────────────────────────────────────────────────────
          if (resolvedPersistentId && !hasRedirectedRef.current) {
            hasRedirectedRef.current = true;
            
            // Count remaining files in the queue
            const remainingCount = queuedFiles.filter(f => 
              f.status !== 'completed' && f.status !== 'failed' && f.id !== fileId
            ).length;

            // Show GLOBAL toast (survives component unmount/navigation)
            toast.success(
              (t) => (
                <div className="flex flex-col gap-1 bg-gray-800 text-white p-4 rounded-lg shadow-lg max-w-sm">
                  <span className="font-semibold text-emerald-400">First file ready!</span>
                  <span className="text-sm text-gray-300">Redirecting to analysis...</span>
                  {remainingCount > 0 && (
                    <span className="text-sm text-gray-400">
                      {remainingCount} other file(s) still processing in the background.
                    </span>
                  )}
                  <button
                    onClick={() => {
                      navigate('/library');
                      toast.dismiss(t.id);
                    }}
                    className="mt-2 text-sm underline text-blue-400 hover:text-blue-300 text-left w-fit"
                  >
                    Go to Library to track progress
                  </button>
                </div>
              ),
              { duration: 8000 } // Keep visible for 8 seconds
            );

            // Delay navigation slightly so the user sees the toast
            setTimeout(() => navigate(`/identities/${resolvedPersistentId}`), 1500);
          }
        } else if (response.status === 'failed') {
          setQueuedFiles(prev => prev.map(f => 
            f.id === fileId ? { ...f, status: 'failed', error: 'Analysis failed' } : f
          ));
        } else if (attempts < maxAttempts) {
          setTimeout(poll, 3000); // Poll every 3 seconds
        } else {
          setQueuedFiles(prev => prev.map(f => 
            f.id === fileId ? { ...f, status: 'failed', error: 'Timeout' } : f
          ));
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          setTimeout(poll, 3000);
        } else {
          setQueuedFiles(prev => prev.map(f => 
            f.id === fileId ? { ...f, status: 'failed', error: 'Connection lost' } : f
          ));
        }
      }
    };
    await poll();
  };

  const uploadSingleFile = async (queuedFile: QueuedFile) => {
    const apiClient = getApiClient();
    try {
      const formData = new FormData();
      formData.append('file', queuedFile.file);
      formData.append('watermark', uploadOptions.watermark);
      if (uploadOptions.customPrompt) formData.append('prompt', uploadOptions.customPrompt);

      // Phase 2.3: Tag upload with active workspace org
      if (orgFilter) {
        formData.append('org_id', orgFilter);
      }

      const response = await apiClient.postFormData<AnalysisResponse>(ENDPOINTS.UPLOAD, formData);
      
      const persistentId = (response as any).identity?.persistent_id;

      setQueuedFiles(prev => prev.map(f => 
        f.id === queuedFile.id ? { ...f, jobId: response.job_id, persistentId, progress: 50 } : f
      ));

      await pollForResults(queuedFile.id, response.job_id, persistentId);
    } catch (error: any) {
      setQueuedFiles(prev => prev.map(f => 
        f.id === queuedFile.id ? { ...f, status: 'failed', error: error.message || 'Upload failed' } : f
      ));
    }
  };

  // ───────────────────────────────────────────────────────────────────
  // URL SUBMISSION HANDLER
  // ───────────────────────────────────────────────────────────────────
  const handleSubmitURL = async () => {
    const trimmedUrl = urlInput.trim();
    if (!trimmedUrl) return;
    
    // Basic URL validation
    if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    
    setIsUrlSubmitting(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post<AnalysisResponse>(ENDPOINTS.UPLOAD_URL, {
        url: trimmedUrl,
        watermark: uploadOptions.watermark,
        prompt: uploadOptions.customPrompt,
        org_id: orgFilter,  
      });
      const persistentId = (response as any).identity?.persistent_id;
      const jobId = response.job_id;
      
      // Add to queue as a virtual file entry so user sees progress
      const virtualFileId = `url-${Date.now()}-${Math.random()}`;
      setQueuedFiles(prev => [...prev, {
        id: virtualFileId,
        file: new File([], trimmedUrl), // Placeholder File object
        preview: undefined,
        status: 'processing',
        progress: 10,
        jobId,
        persistentId,
      }]);
      
      // Reset redirect tracker for this new submission
      hasRedirectedRef.current = false;
      
      // Poll using existing logic (auto-redirect + global toast handled inside)
      pollForResults(virtualFileId, jobId, persistentId);
      
      // Clear input on success
      setUrlInput('');
      toast.success(`URL submitted! Platform: ${(response as any).platform || 'detecting...'}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit URL');
    } finally {
      setIsUrlSubmitting(false);
    }
  };

  // ───────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────
  const hasActiveFiles = queuedFiles.length > 0;

  return (
    <div className="pt-[80px] px-4 sm:px-6 lg:px-8 pb-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-5xl mx-auto w-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Analyze File</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
            Drop files to begin immediate analysis (Max {MAX_FILE_SIZE_MB}MB per file)
          </p>
        </div>

        {/* CONTEXT-AWARE DROPZONE: Hides when files are processing */}
        {!hasActiveFiles ? (
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            className={`relative border-2 border-dashed rounded-xl p-8 sm:p-16 text-center transition-all cursor-pointer ${
              isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 scale-[1.02]' : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-400'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,audio/*"
              onChange={(e) => e.target.files && handleFiles(e.target.files)}
              className="hidden"
            />
            <Upload className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Drop files here or click to browse
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Processing starts immediately upon selection.
            </p>
          </div>
        ) : (
          /* COMPACT "ADD MORE" BUTTON WHEN QUEUE IS ACTIVE */
          <div className="mb-6 flex justify-center">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,audio/*"
                onChange={(e) => e.target.files && handleFiles(e.target.files)}
                className="hidden"
              />
              <Plus className="w-4 h-4" />
              Add More Files
            </button>
          </div>
        )}

        {/* ───────────────────────────────────────────────────────────────── */}
        {/* URL ENTRY BOX - Always visible below file selection              */}
        {/* ───────────────────────────────────────────────────────────────── */}
        <div className="mt-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <Plus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Or paste a URL</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Social media posts, news articles, direct media links</p>
            </div>
          </div>
          <div className="flex gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !isUrlSubmitting && handleSubmitURL()}
              placeholder="https://x.com/.../video/1 or https://youtube.com/watch?v=..."
              disabled={isUrlSubmitting}
              className="flex-1 px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50"
            />
            <button
              onClick={handleSubmitURL}
              disabled={!urlInput.trim() || isUrlSubmitting}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 flex-shrink-0"
            >
              {isUrlSubmitting ? (
                <>
                  <Cpu className="w-4 h-4 animate-spin" />
                  <span className="hidden sm:inline">Submitting...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Analyze URL</span>
                  <span className="sm:hidden">Go</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* QUEUE & OPTIONS PANEL */}
        {hasActiveFiles && (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700 flex">
              <button
                onClick={() => setActiveTab('files')}
                className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'files' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Processing Queue ({queuedFiles.length})
              </button>
              <button
                onClick={() => setActiveTab('options')}
                className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'options' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Scan Options
              </button>
            </div>

            {/* Files Tab */}
            {activeTab === 'files' && (
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Live Status</h3>
                  <button 
                    onClick={() => setQueuedFiles([])} 
                    className="text-xs text-red-500 hover:underline"
                  >
                    Clear All
                  </button>
                </div>

                <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
                  {queuedFiles.map((file) => (
                    <div key={file.id} className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      {/* Icon */}
                      <div className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {file.preview ? (
                          <img src={file.preview} alt="" className="w-full h-full object-cover" />
                        ) : file.file.type.startsWith('video/') ? <FileVideo className="w-5 h-5 text-gray-500" />
                          : file.file.type.startsWith('audio/') ? <FileAudio className="w-5 h-5 text-gray-500" />
                          : <FileText className="w-5 h-5 text-gray-500" />}
                      </div>

                      {/* Info & Progress */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.file.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all ${
                                file.status === 'completed' ? 'bg-emerald-500' :
                                file.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                              }`}
                              style={{ width: `${file.progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 w-10 text-right">{file.progress}%</span>
                        </div>
                      </div>

                      {/* Status Action */}
                      <div className="flex-shrink-0 w-8 flex justify-center">
                        {file.status === 'uploading' || file.status === 'processing' ? (
                          <Cpu className="w-5 h-5 text-blue-500 animate-pulse" />
                        ) : file.status === 'completed' ? (
                          <Check className="w-5 h-5 text-emerald-500" />
                        ) : file.status === 'failed' ? (
                          <span title={file.error}>
                            <AlertCircle className="w-5 h-5 text-red-500" />
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Options Tab */}
            {activeTab === 'options' && (
              <div className="p-4 sm:p-6 space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Watermark Type</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { value: 'invisible', label: 'Invisible', icon: EyeOff },
                      { value: 'visible', label: 'Visible', icon: Eye },
                      { value: 'both', label: 'Both', icon: Shield },
                      { value: 'none', label: 'None', icon: X },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setUploadOptions(prev => ({ ...prev, watermark: opt.value as any }))}
                        className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-colors ${
                          uploadOptions.watermark === opt.value
                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <opt.icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <span className="text-xs font-medium text-gray-900 dark:text-white">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Custom Prompt (Optional)</h4>
                  <textarea
                    value={uploadOptions.customPrompt}
                    onChange={(e) => setUploadOptions(prev => ({ ...prev, customPrompt: e.target.value }))}
                    placeholder="e.g., Focus on detecting deepfake artifacts..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileAnalysis;